"use client"

import * as React from "react"
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from "@/components/ui/label"
import { MessageCircle, Star, Loader2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { whatsappService, type WhatsAppNumber } from "@/services/whatsappService"

interface WhatsAppNumbersListProps {
  /** Bumped by the parent when a connection is added or removed. */
  refreshKey?: number
}

/**
 * Los números de WhatsApp de la empresa, los que contestan.
 *
 * Estos se conectan con el login de Meta y son de la empresa. No confundir con
 * los móviles de los comerciales, que están en `/actions`: aquellos son los de
 * las personas que escriben, y no se conectan a nada.
 *
 * Se puede tener varios. El marcado por defecto es el que contesta cuando nadie
 * nombra uno, así que cederlo es un acto explícito.
 */
export function WhatsAppNumbersList({ refreshKey = 0 }: WhatsAppNumbersListProps) {
  const [numbers, setNumbers] = React.useState<WhatsAppNumber[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [savingId, setSavingId] = React.useState<string | null>(null)
  const [drafts, setDrafts] = React.useState<Record<string, string>>({})

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const list = await whatsappService.getConnections()
      setNumbers(list)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load, refreshKey])

  const nameOf = (number: WhatsAppNumber) =>
    drafts[number.id] ?? number.display_name

  const save = async (
    number: WhatsAppNumber,
    changes: { display_name?: string; is_default?: boolean; is_internal?: boolean }
  ) => {
    setSavingId(number.id)
    try {
      await whatsappService.updateConnection(number.id, changes)
      await load()
      setDrafts((current) => {
        const next = { ...current }
        delete next[number.id]
        return next
      })
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Cargando números…</span>
      </div>
    )
  }

  if (numbers.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <ul className="space-y-2">
        {numbers.map((number) => (
          <li
            key={number.id}
            className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
          >
            <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />

            <Input
              className="h-8 w-48"
              value={nameOf(number)}
              placeholder="Sin nombre"
              onChange={(event) =>
                setDrafts((current) => ({ ...current, [number.id]: event.target.value }))
              }
              onBlur={() => {
                const name = nameOf(number).trim()
                if (name !== number.display_name) {
                  void save(number, { display_name: name })
                }
              }}
              disabled={savingId === number.id}
            />

            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {number.display_phone_number || number.phone_number_id}
            </span>

            {number.is_default ? (
              <Badge variant="secondary" className="gap-1">
                <Star className="h-3 w-3" />
                Por defecto
              </Badge>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={savingId === number.id}
                onClick={() => void save(number, { is_default: true })}
              >
                Hacer por defecto
              </Button>
            )}

            <Label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={number.is_internal}
                disabled={savingId === number.id}
                onCheckedChange={(valor) => void save(number, { is_internal: valor === true })}
              />
              Interno
            </Label>

            {!number.connected && <Badge variant="outline">Desconectado</Badge>}
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">
        Estos son los números de la empresa, los que contestan. El marcado por defecto
        es el que responde cuando nadie nombra uno.
      </p>

      <p className="text-xs text-muted-foreground">
        Un número interno solo atiende a los comerciales cuyo móvil esté declarado en{" "}
        <code>/actions</code>; a quien no lo esté, no le abre conversación.
      </p>

      <p className="text-xs text-muted-foreground">
        Quién contesta por cada número se elige en <strong>Canales por agente</strong>,
        donde cada número tiene su propia fila.
      </p>
    </div>
  )
}
